import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';
import { AiOutlinePlus } from 'react-icons/ai';
import {
  Button,
  ButtonGroup,
  ButtonProps,
  Flex,
  Group,
  Menu,
  Portal,
  Stack,
} from '@chakra-ui/react';

import { useOpenClose, UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useCan } from '~/core/shared/hooks/useCan';
import {
  DocumentMessage,
  DocumentMessagesPrivateFilterOptions,
  FilterOptions,
} from '~/core/shared/types';
import { isDeniedHook } from '~/core/shared/utils/general';
import { useMiddleOfficeConfig } from '../../../contexts/middleOfficeConfig';
import { ApplicationMessagesTable } from '../../messages/ApplicationMessagesTable.component';
import { ApplicationContext } from '../Layout.component';

type MessagesTabContextType = {
  filterOptions: FilterOptions<DocumentMessage>;
  setFilterOptions: Dispatch<SetStateAction<FilterOptions<DocumentMessage>>>;
  emailCreateModalApi: UseOpenCloseReturn;
  noteCreateModalApi: UseOpenCloseReturn;
};

const getFilterButtonStyles = (isSelected: boolean): ButtonProps => ({
  ...(isSelected
    ? {
        colorPalette: 'primary',
        fontWeight: 'bold',
        variant: 'solid',
        border: 'none',
      }
    : {
        colorPalette: 'black',
        fontWeight: 'normal',
        variant: 'outline',
      }),
});

export const MessagesTabContext = createContext<MessagesTabContextType>(
  {} as MessagesTabContextType,
);

export const MessagesTab: FC = () => {
  const { canAll, canAny } = useCan();
  const [filterOptions, setFilterOptions] = useState<
    FilterOptions<DocumentMessage>
  >({ private: 'all' as DocumentMessagesPrivateFilterOptions });
  const privateFilterOptions: Array<DocumentMessagesPrivateFilterOptions> = [
    'all',
    'private',
    'public',
  ];
  const emailCreateModalApi = useOpenClose();
  const noteCreateModalApi = useOpenClose();
  const middleOfficeConfig = useMiddleOfficeConfig();

  const messagesTabContextValue = useMemo(
    () => ({
      filterOptions,
      setFilterOptions,
      emailCreateModalApi,
      noteCreateModalApi,
    }),
    [filterOptions, setFilterOptions, emailCreateModalApi, noteCreateModalApi],
  );

  const { hooks } = useContext(ApplicationContext);
  const disabled = isDeniedHook(hooks, 'messageStore');

  return (
    <MessagesTabContext.Provider value={messagesTabContextValue}>
      <Stack gap={8}>
        <Flex justifyContent="space-between">
          {privateFilterOptions.length > 2 && (
            <ButtonGroup size="md" variant="outline">
              <Group attached colorPalette="gray">
                {privateFilterOptions.map((privateFilterOption, idx) => (
                  <Button
                    {...getFilterButtonStyles(
                      privateFilterOption === filterOptions.private,
                    )}
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    borderColor="black"
                    color="black"
                    fontSize="md"
                    onClick={() =>
                      setFilterOptions({
                        ...filterOptions,
                        private: privateFilterOption,
                      })
                    }
                    textTransform="capitalize"
                  >
                    {privateFilterOption}
                  </Button>
                ))}
              </Group>
            </ButtonGroup>
          )}
          {canAny([
            Permissions.MessagesCreate,
            Permissions.MessagesEmailCreate,
          ]) && (
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button
                  borderColor="black"
                  color="black"
                  colorPalette="gray"
                  disabled={disabled}
                  fontSize="md"
                  fontWeight={400}
                  size="md"
                  variant="outline"
                >
                  <AiOutlinePlus />
                  Add new
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    {canAll([Permissions.MessagesCreate]) && (
                      <Menu.Item
                        onClick={noteCreateModalApi.onOpen}
                        value="note"
                      >
                        Note
                      </Menu.Item>
                    )}
                    {canAll([Permissions.MessagesEmailCreate]) && (
                      <Menu.Item
                        onClick={emailCreateModalApi.onOpen}
                        value="email"
                      >
                        Email to{' '}
                        {middleOfficeConfig.getLabel(
                          'messageTab',
                          'mailTo',
                          'customer',
                        )}
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          )}
        </Flex>
        <ApplicationMessagesTable />
      </Stack>
    </MessagesTabContext.Provider>
  );
};
